import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of, distinctUntilChanged, map } from 'rxjs';

/**
 * State for a single call session's media streams
 */
export interface SessionMediaState {
    /** Remote video stream from the peer */
    videoStream: MediaStream | null;
    /** Remote audio stream from the peer */
    audioStream: MediaStream | null;
    /** Local audio stream (microphone) */
    localAudioStream: MediaStream | null;
    /** Local video stream (for future use) */
    localVideoStream: MediaStream | null;
}

function createEmptySessionState(): SessionMediaState {
    return {
        videoStream: null,
        audioStream: null,
        localAudioStream: null,
        localVideoStream: null
    };
}

/**
 * MediaStreamStore - Manages MediaStream objects for WebRTC calls
 *
 * This service decouples CallSessionManager and RTCViewer from the UI layer.
 * Instead of directly manipulating DOM elements, RTCViewer emits MediaStreams
 * which are stored here. Components subscribe to selectors and handle their
 * own video/audio element attachment.
 *
 * Two slots are provided:
 * - first: The first session established (stays bound to the main video element;
 *          when put on hold the CSS animates it to the pip position)
 * - second: The second session (e.g. forward target, shown in the secondary video element)
 */
@Injectable({ providedIn: 'root' })
export class MediaStreamStore {

    /** Whether the browser blocked audio autoplay and needs user interaction */
    private autoplayBlocked$ = new BehaviorSubject<boolean>(false);

    selectAutoplayBlocked(): Observable<boolean> {
        return this.autoplayBlocked$.pipe(distinctUntilChanged());
    }

    setAutoplayBlocked(blocked: boolean): void {
        this.autoplayBlocked$.next(blocked);
    }

    /** Slot for the first session established */
    private firstSessionStreams$ = new BehaviorSubject<SessionMediaState>(createEmptySessionState());

    /** Slot for the second session (forward target) */
    private secondSessionStreams$ = new BehaviorSubject<SessionMediaState>(createEmptySessionState());

    //#region First Session Selectors

    selectFirstVideoStream(): Observable<MediaStream | null> {
        return this.firstSessionStreams$.pipe(
            map(s => s.videoStream),
            distinctUntilChanged()
        );
    }

    selectFirstAudioStream(): Observable<MediaStream | null> {
        return this.firstSessionStreams$.pipe(
            map(s => s.audioStream),
            distinctUntilChanged()
        );
    }

    selectFirstLocalAudioStream(): Observable<MediaStream | null> {
        return this.firstSessionStreams$.pipe(
            map(s => s.localAudioStream),
            distinctUntilChanged()
        );
    }

    selectFirstLocalVideoStream(): Observable<MediaStream | null> {
        return this.firstSessionStreams$.pipe(
            map(s => s.localVideoStream),
            distinctUntilChanged()
        );
    }

    //#endregion First Session Selectors

    //#region Second Session Selectors

    selectSecondVideoStream(): Observable<MediaStream | null> {
        return this.secondSessionStreams$.pipe(
            map(s => s.videoStream),
            distinctUntilChanged()
        );
    }

    selectSecondAudioStream(): Observable<MediaStream | null> {
        return this.secondSessionStreams$.pipe(
            map(s => s.audioStream),
            distinctUntilChanged()
        );
    }

    selectSecondLocalAudioStream(): Observable<MediaStream | null> {
        return this.secondSessionStreams$.pipe(
            map(s => s.localAudioStream),
            distinctUntilChanged()
        );
    }

    selectSecondLocalVideoStream(): Observable<MediaStream | null> {
        return this.secondSessionStreams$.pipe(
            map(s => s.localVideoStream),
            distinctUntilChanged()
        );
    }

    //#endregion Second Session Selectors

    //#region First Session Setters

    setFirstVideoStream(stream: MediaStream | null): void {
        const current = this.firstSessionStreams$.value;
        this.firstSessionStreams$.next({ ...current, videoStream: stream });
    }

    setFirstAudioStream(stream: MediaStream | null): void {
        const current = this.firstSessionStreams$.value;
        this.firstSessionStreams$.next({ ...current, audioStream: stream });
    }

    setFirstLocalAudioStream(stream: MediaStream | null): void {
        const current = this.firstSessionStreams$.value;
        this.firstSessionStreams$.next({ ...current, localAudioStream: stream });
    }

    setFirstLocalVideoStream(stream: MediaStream | null): void {
        const current = this.firstSessionStreams$.value;
        this.firstSessionStreams$.next({ ...current, localVideoStream: stream });
    }

    //#endregion First Session Setters

    //#region Second Session Setters

    setSecondVideoStream(stream: MediaStream | null): void {
        const current = this.secondSessionStreams$.value;
        this.secondSessionStreams$.next({ ...current, videoStream: stream });
    }

    setSecondAudioStream(stream: MediaStream | null): void {
        const current = this.secondSessionStreams$.value;
        this.secondSessionStreams$.next({ ...current, audioStream: stream });
    }

    setSecondLocalAudioStream(stream: MediaStream | null): void {
        const current = this.secondSessionStreams$.value;
        this.secondSessionStreams$.next({ ...current, localAudioStream: stream });
    }

    setSecondLocalVideoStream(stream: MediaStream | null): void {
        const current = this.secondSessionStreams$.value;
        this.secondSessionStreams$.next({ ...current, localVideoStream: stream });
    }

    //#endregion Second Session Setters

    //#region Per-Call Stream Store

    /** Per-call media state, keyed by callId */
    private callStreams = new Map<string, BehaviorSubject<SessionMediaState>>();

    private getOrCreateCallSubject(callId: string): BehaviorSubject<SessionMediaState> {
        let subj = this.callStreams.get(callId);
        if (!subj) {
            subj = new BehaviorSubject<SessionMediaState>(createEmptySessionState());
            this.callStreams.set(callId, subj);
        }
        return subj;
    }

    selectCallVideoStream(callId: string | null): Observable<MediaStream | null> {
        if (!callId) return of(null);
        return this.getOrCreateCallSubject(callId).pipe(
            map(s => s.videoStream),
            distinctUntilChanged()
        );
    }

    selectCallAudioStream(callId: string | null): Observable<MediaStream | null> {
        if (!callId) return of(null);
        return this.getOrCreateCallSubject(callId).pipe(
            map(s => s.audioStream),
            distinctUntilChanged()
        );
    }

    setCallVideoStream(callId: string, stream: MediaStream | null): void {
        const subj = this.getOrCreateCallSubject(callId);
        subj.next({ ...subj.value, videoStream: stream });
    }

    setCallAudioStream(callId: string, stream: MediaStream | null): void {
        const subj = this.getOrCreateCallSubject(callId);
        subj.next({ ...subj.value, audioStream: stream });
    }

    removeCallStreams(callId: string): void {
        const subj = this.callStreams.get(callId);
        if (subj) {
            subj.next(createEmptySessionState());
            subj.complete();
            this.callStreams.delete(callId);
        }
    }

    //#endregion Per-Call Stream Store

    //#region Utility Methods

    /**
     * Reset all streams (clear both first and second slots)
     */
    reset(): void {
        this.firstSessionStreams$.next(createEmptySessionState());
        this.secondSessionStreams$.next(createEmptySessionState());
        this.autoplayBlocked$.next(false);
    }

    /**
     * Reset only the first session streams
     */
    resetFirst(): void {
        this.firstSessionStreams$.next(createEmptySessionState());
    }

    /**
     * Reset only the second session streams
     */
    resetSecond(): void {
        this.secondSessionStreams$.next(createEmptySessionState());
    }

    //#endregion Utility Methods
}
